import { httpsCallable } from 'firebase/functions';
import React, { useEffect, useState } from 'react';
import { Functions } from 'src/contexts/FirebaseContext';
import { useCustomer } from 'src/hooks/useUserActivity';
import { useSelector } from 'src/redux/store';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Download, ChevronDown } from 'lucide-react';
import { Separator } from '../ui/separator';
import { Skeleton } from '../ui/skeleton';
import { format } from 'date-fns';

async function listCustomerInvoices(startingAfter?: string) {
  try {
    const functionRef = httpsCallable(Functions, 'settings');
    const response: any = await functionRef({
      request: 'STRIPE_LIST_INVOICES',
      startingAfter,
    });

    const error = response?.data?.httpErrorCode?.status || response?.data?.errorInfo?.code;
    if (error) {
      const errorMessage =
        response?.data?.errorInfo?.message || response?.data?.httpErrorCode?.canonicalName;
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }
}

export default function CustomerInvoices() {
  const userId = useSelector((state) => state.user.id);
  const { customer } = useCustomer(userId);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  console.log('customer', customer);
  const handleFetchInvoices = async (startingAfter?: string) => {
    if (!customer?.stripeId) return;

    if (startingAfter) {
      setIsFetchingMore(true);
    } else {
      setIsLoading(true);
    }

    try {
      const response = await listCustomerInvoices(startingAfter);
      if (response) {
        setInvoices((prev) => (startingAfter ? [...prev, ...response.data] : response.data));
        setHasMore(response.hasMore);
      }
    } catch (error) {
      // Error is already logged and toasted in the service function
    } finally {
      if (startingAfter) {
        setIsFetchingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (customer?.stripeId) {
      handleFetchInvoices();
    } else {
      setIsLoading(false);
    }
  }, [customer?.stripeId]);

  return (
    <div>
      <div role="button" className="flex cursor-pointer items-center justify-between">
        <h3 className="text-lg font-semibold">Invoice History</h3>
      </div>

      <div className="mt-4">
        {isLoading && invoices.length === 0 ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center justify-between py-2">
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="flex items-center gap-4">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-8 w-16" />
                  <Skeleton className="h-9 w-9" />
                </div>
              </div>
            ))}
          </div>
        ) : invoices.length === 0 ? (
          <p className="text-center text-muted-foreground">No invoices found.</p>
        ) : (
          <>
            {invoices.map((invoice, index) => (
              <div key={invoice.id}>
                <div className="flex items-center justify-between py-2">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{invoice.number}</span>
                    <span className="text-xs text-muted-foreground">
                      {format(invoice.created * 1000, 'dd/MM/yyyy')}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm">
                      {invoice.amountDue / 100} {invoice.currency.toUpperCase()}
                    </span>
                    <Badge
                      variant={
                        invoice.status === 'paid'
                          ? 'default'
                          : invoice.status === 'overdue'
                          ? 'destructive'
                          : 'secondary'
                      }
                      className={
                        invoice.status === 'paid'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
                          : ''
                      }
                    >
                      {invoice.status}
                    </Badge>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => window.open(invoice.pdf, '_blank')}
                    >
                      <Download className="h-4 w-4" />
                      <span className="sr-only">Download invoice</span>
                    </Button>
                  </div>
                </div>
                {index < invoices.length - 1 && <Separator />}
              </div>
            ))}
            {hasMore && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => handleFetchInvoices(invoices[invoices.length - 1].id)}
                  disabled={isFetchingMore}
                >
                  {isFetchingMore ? 'Loading...' : 'Load More'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
